if (!customElements.get('tr-extra-products-cart')) {
	class TrExtraProductsCart extends HTMLElement {
		selectors = {
			form: 'form',
			submitButton: '[type="submit"]',
			submitButtonText: '[type="submit"] span',
			loadingSpinner: '.loading__spinner',
			errorMessageWrapper: '.product-form__error-message-wrapper',
			errorMessage: '.product-form__error-message',
			variantIdInput: 'input[name="id"]',
			quantityInput: '.quantity__input'
		};

		stateClasses = {
			loading: 'loading',
			hidden: 'hidden',
			isEmpty: 'is-empty'
		};

		isAddingToCart = false;

		connectedCallback() {
			this.form = this.querySelector(this.selectors.form);
			this.submitButton = this.querySelector(this.selectors.submitButton);
			this.submitButtonText = this.querySelector(this.selectors.submitButtonText);
			this.loadingSpinner = this.querySelector(this.selectors.loadingSpinner);
			this.variantIdInput = this.querySelector(this.selectors.variantIdInput);
			this.cart =
				document.querySelector('cart-notification') ||
				document.querySelector('cart-drawer');

			if (this.variantIdInput) this.variantIdInput.disabled = false;

			if (this.cart && this.submitButton) {
				this.submitButton.setAttribute('aria-haspopup', 'dialog');
			}

			this.form?.addEventListener('submit', this.onSubmit);
		}

		disconnectedCallback() {
			this.form?.removeEventListener('submit', this.onSubmit);
		}

		onSubmit = (event) => {
			event.preventDefault();
			this.addToCart();
		};

		getBundleId() {
			if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
				return crypto.randomUUID();
			}

			return `steamer-${Date.now()}-${Math.random().toString(16).slice(2)}`;
		}

		get productInfoElement() {
			return this.closest('product-info');
		}

		get extraProductsElement() {
			return this.productInfoElement?.querySelector('tr-extra-products');
		}

		get quantity() {
			const quantityInput = this.productInfoElement?.querySelector(this.selectors.quantityInput);
			const quantity = Number(quantityInput?.value) || 1;

			return quantity > 0 ? quantity : 1;
		}

		getParentPriceCents() {
			const extraPrice = Number(this.extraProductsElement?.variantPrice) || 0;

			if (extraPrice) return extraPrice;

			const priceRoot = this.productInfoElement?.querySelector('[data-tr-js-variant-price]');

			return Number(priceRoot?.getAttribute('data-tr-js-variant-price')) || 0;
		}

		getGiftProperties() {
			const gift = this.productInfoElement?.querySelector('tr-make-it-a-gift')?.getGiftState?.() || {
				enabled: false,
				message: ''
			};

			return {
				_makeItGift: gift.enabled ? 'true' : 'false',
				...(gift.enabled ? { _giftMessage: gift.message } : {})
			};
		}

		buildItemProperties({ role, price, bundleId, parentVariantId, parentPrice, extra = {} }) {
			return {
				_bundleId: bundleId,
				_role: role,
				_parentProduct: String(parentVariantId),
				_parentPrice: String(parentPrice),
				_price: String(price),
				...this.getGiftProperties(),
				...extra
			};
		}

		buildCartItems() {
			const parentVariantId = Number(this.variantIdInput?.value) || 0;

			if (!parentVariantId) return [];

			const extras = this.extraProductsElement?.getSelectedExtras?.() || {
				seasonings: [],
				addOn: null
			};
			const seasonings = extras.seasonings || [];
			const addOn = extras.addOn || null;
			const parentPriceCents = this.getParentPriceCents();
			const seasoningPriceCents = seasonings.reduce(
				(total, seasoning) => total + (seasoning.priceCents || 0),
				0
			);
			const addOnPriceCents = addOn?.priceCents || 0;
			const parentPrice = parentPriceCents + seasoningPriceCents + addOnPriceCents;
			const bundleId = this.getBundleId();
			const quantity = this.quantity;
			const itemContext = {
				bundleId,
				parentVariantId,
				parentPrice
			};

			return [
				{
					id: parentVariantId,
					quantity,
					properties: this.buildItemProperties({
						...itemContext,
						role: 'parent',
						price: parentPriceCents
					})
				},
				...seasonings.map((seasoning, index) => ({
					id: seasoning.variantId,
					quantity,
					properties: this.buildItemProperties({
						...itemContext,
						role: 'seasoning',
						price: seasoning.priceCents,
						extra: { _seasoningIndex: String(index) }
					})
				})),
				...(addOn
					? [
							{
								id: addOn.variantId,
								quantity,
								properties: this.buildItemProperties({
									...itemContext,
									role: 'add_on',
									price: addOn.priceCents,
									extra: addOn.label ? { _addOnLabel: addOn.label } : {}
								})
							}
					  ]
					: [])
			];
		}

		setAddToCartBusy(isBusy) {
			if (!this.submitButton) return;

			this.submitButton.toggleAttribute('aria-disabled', isBusy);
			this.submitButton.classList.toggle(this.stateClasses.loading, isBusy);
			this.loadingSpinner?.classList.toggle(this.stateClasses.hidden, !isBusy);
		}

		async addToCart() {
			if (this.isAddingToCart || this.submitButton?.getAttribute('aria-disabled') === 'true') return;
			if (this.submitButton?.hasAttribute('disabled')) return;

			const items = this.buildCartItems();

			if (!items.length) {
				this.handleErrorMessage(window.cartStrings?.error);
				return;
			}

			this.isAddingToCart = true;
			this.setAddToCartBusy(true);
			this.handleErrorMessage();

			const config =
				typeof fetchConfig === 'function'
					? fetchConfig('javascript')
					: {
							method: 'POST',
							headers: {
								'Content-Type': 'application/json',
								Accept: 'application/javascript'
							}
					  };
			const body = { items };

			if (this.cart) {
				body.sections = this.cart
					.getSectionsToRender()
					.map((section) => section.id)
					.join(',');
				body.sections_url = window.location.pathname;
				this.cart.setActiveElement?.(document.activeElement);
			}

			try {
				const cartAddUrl = window.routes?.cart_add_url || '/cart/add.js';
				const response = await fetch(cartAddUrl.endsWith('.js') ? cartAddUrl : `${cartAddUrl}.js`, {
					...config,
					body: JSON.stringify(body)
				});
				const data = await response.json();

				if (data.status) {
					this.handleErrorMessage(data.description || data.message || window.cartStrings?.error);
					return;
				}

				if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
					publish(PUB_SUB_EVENTS.cartUpdate, {
						source: 'cart-items',
						cartData: data
					});
				}

				if (this.cart && data.sections) {
					if (!data.key && data.items?.[0]?.key) {
						data.key = data.items[0].key;
					}

					this.cart.classList.remove(this.stateClasses.isEmpty);
					this.cart.querySelector('.drawer__inner')?.classList.remove(this.stateClasses.isEmpty);
					this.cart.renderContents(data);
					return;
				}

				window.location = window.routes?.cart_url || '/cart';
			} catch (error) {
				this.handleErrorMessage(error.message || window.cartStrings?.error);
			} finally {
				this.isAddingToCart = false;
				this.setAddToCartBusy(false);
			}
		}

		handleErrorMessage(errorMessage = false) {
			this.errorMessageWrapper =
				this.errorMessageWrapper || this.querySelector(this.selectors.errorMessageWrapper);

			if (!this.errorMessageWrapper) return;

			this.errorMessage =
				this.errorMessage || this.errorMessageWrapper.querySelector(this.selectors.errorMessage);
			this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

			if (errorMessage) {
				this.errorMessage.textContent = errorMessage;
			}
		}

		toggleSubmitButton(disable = true, text) {
			if (!this.submitButton) return;

			if (disable) {
				this.submitButton.setAttribute('disabled', 'disabled');
				if (text && this.submitButtonText) this.submitButtonText.textContent = text;
				return;
			}

			this.submitButton.removeAttribute('disabled');
			if (this.submitButtonText) {
				this.submitButtonText.textContent = text || window.variantStrings?.addToCart;
			}
		}
	}

	customElements.define('tr-extra-products-cart', TrExtraProductsCart);
}